import { useReducer } from "react";
import createUseContext from "constate";

// Define the initial state of our app
const getInitialState = () => ({
  currentCard: null,
  cards: [],
  ratios: [],
  includedCount: null,
  lastSaleDate: null,
  loading: false,
  error: null,
});

// Define a pure function reducer
const reducer = (state, action) => {
  switch (action.type) {
    case "SET_CARDS":
      return {
        ...state,
        cards: action.payload,
      };
    case "SET_RATIOS":
      return {
        ...state,
        ratios: action.payload,
      };
    case "SET_INCLUDED_COUNT":
      return {
        ...state,
        includedCount: action.payload,
      };
    case "SET_LAST_SALE_DATE":
      return {
        ...state,
        lastSaleDate: action.payload,
      };
    case "ADD_CARD":
      return {
        ...state,
        cards: [...state.cards, action.payload],
      };
    case "UPDATE_CARD": {
      const { currentCard } = state;
      const newCards = [...state.cards];
      const updatedCard = action.payload;
      const cardIndex = newCards.findIndex((card) => card.id === updatedCard.id);
      if (cardIndex > -1) {
        newCards[cardIndex] = updatedCard;
      }
      return {
        ...state,
        currentCard: currentCard && currentCard.id === updatedCard.id ? updatedCard : currentCard,
        cards: newCards,
      };
    }
    case "SET_HAS_ALERT": {
      return {
        ...state,
        cards: state.cards.map((c) => {
          if (c.id === Number(action.payload.id)) {
            c.has_alert = true;
          }

          return c;
        }),
      };
    }
    case "DEL_CARD":
      return {
        ...state,
        cards: state.cards.filter((card) => card.id !== action.payload),
      };
    case "START":
      return {
        ...state,
        loading: true,
      };
    case "COMPLETE":
      return {
        ...state,
        loading: false,
      };
    case "SET_CURRENT":
      return {
        ...state,
        cards: state.cards,
        currentCard: action.payload,
      };
    case "UPDATE_CARDS_BY_SET_ID":
      return {
        ...state,
        cards: state.cards.map((card) => {
          if (card.set_id === action.payload.setId) {
            return {
              ...card,
              ...action.payload.update,
            };
          }
          return card;
        }),
      };
    case "UPDATE_CARDS_BY_SET_VARIATION_ID":
      return {
        ...state,
        cards: state.cards.map((card) => {
          if (card.set_variation_id === action.payload.svId) {
            return {
              ...card,
              ...action.payload.update,
            };
          }
          return card;
        }),
      };
    case "RESET":
      return action.payload;
    default:
      throw new Error();
  }
};

// Define your custom hook that contains your state, dispatcher and actions
const useCards = () => {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const { cards, ratios, currentCard, loading, includedCount, lastSaleDate } = state;

  const updateCard = (updatedCard) => {
    dispatch({
      type: "UPDATE_CARD",
      payload: updatedCard,
    });
  };

  const addCard = (card) => {
    dispatch({
      type: "ADD_CARD",
      payload: card,
    });
  };

  const delCard = (id) => {
    dispatch({
      type: "DEL_CARD",
      payload: id,
    });
  };

  const setCurrent = (card) => {
    dispatch({
      type: "SET_CURRENT",
      payload: card,
    });
  };

  const setCards = (cards) => {
    dispatch({
      type: "SET_CARDS",
      payload: cards,
    });
  };

  const setRatios = (ratios) => {
    dispatch({
      type: "SET_RATIOS",
      payload: ratios,
    });
  };

  const setIncludedCount = (count) => {
    dispatch({
      type: "SET_INCLUDED_COUNT",
      payload: count,
    });
  };

  const setLastSaleDate = (date) => {
    dispatch({
      type: "SET_LAST_SALE_DATE",
      payload: date,
    });
  };

  const reset = () => {
    dispatch({
      type: "RESET",
      payload: getInitialState(),
    });
  };

  const updateCardsBySetId = (setId, update) => {
    dispatch({
      type: "UPDATE_CARDS_BY_SET_ID",
      payload: { setId, update },
    });
  };

  const updateCardsBySetVariationId = (svId, update) => {
    dispatch({
      type: "UPDATE_CARDS_BY_SET_VARIATION_ID",
      payload: { svId, update },
    });
  };

  const setHasAlert = (id) => {
    dispatch({
      type: "SET_HAS_ALERT",
      payload: { id },
    });
  };

  return {
    cards,
    ratios,
    currentCard,
    loading,
    includedCount,
    lastSaleDate,
    addCard,
    updateCard,
    delCard,
    setCurrent,
    setCards,
    setRatios,
    setIncludedCount,
    setLastSaleDate,
    reset,
    updateCardsBySetId,
    updateCardsBySetVariationId,
    setHasAlert,
  };
};

// Share your custom hook
const [CardsProvider, useCardsContext] = createUseContext(useCards);

export { CardsProvider, useCardsContext };
